// ============================================================
// 店舗の表記（お客様向け文面のブランド・店名）— 2026-10-03 宮古の点検
//
// 定型メール・AI 起案の多くが「Nレンタカー 那覇空港店」を直書きしていて、
// 宮古の画面でも件名・本文が那覇のまま（署名だけ宮古）だった。
// 文面は那覇の表記で書かれている前提で、**出口で**店舗の表記に置き換える（取りこぼしを防ぐ）。
// 店名・連絡先の正は instances.ts の branding / booking-platform/md/stores.md。
// ============================================================
import { getStoreKey } from './backend';

export interface StoreBrand {
  tagJa: string;   // 件名の【】や「〇〇です。」に使う短いブランド
  tagEn: string;   // [N RENT A CAR] のような英字タグ
  name: { ja: string; en: string; tw: string; kr: string };
}

export const STORE_BRANDS: Record<'ncar' | 'pit', StoreBrand> = {
  ncar: {
    tagJa: 'Nレンタカー', tagEn: 'N RENT A CAR',
    name: { ja: 'Nレンタカー 那覇空港店', en: 'N RENT A CAR Naha Airport', tw: 'N RENT A CAR 那霸機場店', kr: 'N 렌터카 나하 공항점' },
  },
  pit: {
    tagJa: 'ピットレンタカー', tagEn: 'PIT RENT A CAR',
    // tw/kr は new-n-car api/site_config.php の store_name と同じ
    name: { ja: 'ピットレンタカー 宮古空港店', en: 'PIT RENT A CAR Miyako Airport', tw: 'PIT租車 宮古機場店', kr: 'PIT 렌터카 미야코 공항점' },
  },
};

export function getStoreBrand(): StoreBrand {
  return STORE_BRANDS[getStoreKey()] ?? STORE_BRANDS.ncar;
}

/**
 * 那覇の表記で書かれた文面を、いまの店舗の表記に置き換える（那覇の画面では何もしない）。
 * 長い表記から先に置き換える（「Nレンタカー 那覇空港店」→ 店名、その後に残った「Nレンタカー」→ ブランド）。
 */
export function localizeBrand(text: string, storeKey: 'ncar' | 'pit' = getStoreKey()): string {
  if (storeKey === 'ncar' || !text) return text;
  const from = STORE_BRANDS.ncar;
  const to = STORE_BRANDS[storeKey];
  return String(text)
    .split(from.name.ja).join(to.name.ja)
    .split('Nレンタカー那覇空港店').join(to.name.ja.replace(' ', ''))
    .split(from.name.en).join(to.name.en)
    .split('N RENT A CAR NAHA AIRPORT').join(to.name.en.toUpperCase())
    .split('N Rent-A-Car Naha Airport').join(to.name.en)
    .split(from.name.tw).join(to.name.tw)
    .split(from.name.kr).join(to.name.kr)
    .split(`[${from.tagEn}]`).join(`[${to.tagEn}]`)
    .split(`【${from.tagJa}】`).join(`【${to.tagJa}】`)
    .split(`${from.tagJa}です`).join(`${to.tagJa}です`)
    .split(`${from.tagJa}から`).join(`${to.tagJa}から`);
}

/**
 * 宮古の文面に那覇の固有情報が残っていないか（電話・LINE・サイト・店名）。出口の最終確認用
 */
export function findOtherStoreTokens(text: string, storeKey: 'ncar' | 'pit' = getStoreKey()): string[] {
  if (storeKey !== 'pit') return [];
  const naha = ['050-3363-7144', '+81-50-3363-7144', 'lin.ee/pkQzUiG', 'n-car.jp', '那覇空港店', 'Naha Airport', '高良3'];
  return naha.filter((t) => String(text || '').includes(t));
}
